if (!customElements.get('fms-mobile-purchase')) {
  customElements.define('fms-mobile-purchase', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) return;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      };
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.productInfo.addEventListener('input', this.schedule, { signal });
      this.productInfo.addEventListener('change', this.schedule, { signal });
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      document.addEventListener('focusin', this.schedule, { signal });
      document.addEventListener('focusout', this.schedule, { signal });
      this.mobile.addEventListener('change', this.schedule, { signal });
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(document.body, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['class', 'hidden', 'open', 'style', 'aria-hidden', 'inert', 'disabled', 'aria-disabled', 'min', 'max', 'step', 'value'],
      });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this);
      this.unsubscribeQuantity = typeof subscribe === 'function'
        ? subscribe(PUB_SUB_EVENTS.quantityUpdate, this.schedule) : null;
      this.sync();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.unsubscribeQuantity?.();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.restoreFocus = null;
      this.overlayOpen = false;
      this.addAttempted = false;
      document.body.classList.remove('fms-mobile-purchase-enabled');
      document.documentElement.style.removeProperty('--fms-mobile-purchase-space');
    }

    visible(element) {
      if (!element || !element.getClientRects().length) return false;
      for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
        if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true') return false;
        if (node.matches('details:not([open])') && !node.querySelector('summary')?.contains(element)) return false;
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return true;
    }

    blockedByOverlay() {
      if (document.body.classList.contains('cmm-drawer-open')) return true;
      const selector = '.cmm-mobile-drawer.is-open,header-drawer details[open],details-modal details[open],cart-drawer.active,cart-notification.active,dialog[open],[role="dialog"][aria-modal="true"],[role="alertdialog"][aria-modal="true"]';
      return Array.from(document.querySelectorAll(selector)).some((element) => this.visible(element));
    }

    setText(selector, value) {
      const node = this.querySelector(selector);
      if (node.textContent !== value) node.textContent = value;
    }

    sync() {
      const form = document.getElementById(this.dataset.formId);
      this.originalSubmit = form?.querySelector('[name="add"]');
      this.originalQuantity = form?.elements.namedItem('quantity');
      const ready = this.mobile.matches && this.originalSubmit && this.visible(this.originalSubmit);
      if (document.body.classList.contains('fms-mobile-purchase-enabled') !== !!ready) {
        document.body.classList.toggle('fms-mobile-purchase-enabled', !!ready);
      }
      if (!ready) { this.hidden = true; return; }

      const picker = this.productInfo.querySelector('variant-selects');
      const options = picker ? Array.from(picker.querySelectorAll('input[type="radio"]:checked,select')).map((input) => input.value).join(' / ') : '';
      this.setText('[data-option-text]', options || this.dataset.defaultTitle);
      this.querySelector('[data-edit-options]').disabled = !picker;
      const priceRoot = this.productInfo.querySelector('[id^="price-"]');
      const price = priceRoot?.querySelector('.price--on-sale .price-item--sale') || priceRoot?.querySelector('.price__regular .price-item--regular:not(s)');
      this.setText('[data-price]', priceRoot && !priceRoot.classList.contains('hidden') ? price?.textContent.trim() || '' : '');
      const unit = this.originalQuantity?.closest('quantity-input')?.querySelector('.fms-yardage-unit')?.textContent.trim();
      this.setText('[data-quantity]', `${this.originalQuantity?.value || '1'}${unit ? ` ${unit}` : ''}`);
      this.querySelector('[data-edit-quantity]').disabled = !this.originalQuantity;
      const stickySubmit = this.querySelector('[data-submit]');
      stickySubmit.disabled = this.originalSubmit.disabled || this.originalSubmit.getAttribute('aria-disabled') === 'true';
      const loading = this.originalSubmit.classList.contains('loading');
      stickySubmit.classList.toggle('loading', loading);
      stickySubmit.setAttribute('aria-busy', String(loading));
      this.querySelector('.loading__spinner').classList.toggle('hidden', !loading);
      this.setText('[data-submit-text]', this.originalSubmit.querySelector('span')?.textContent.trim() || this.originalSubmit.textContent.trim());

      const rect = this.originalSubmit.getBoundingClientRect();
      const editing = this.productInfo.contains(document.activeElement) && !this.contains(document.activeElement) && document.activeElement.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]),textarea');
      const overlayOpen = this.blockedByOverlay();
      if (overlayOpen && document.querySelector('cart-drawer.active,cart-notification.active')) this.addAttempted = false;
      this.hidden = rect.bottom > 0 || overlayOpen || editing;
      if (!this.hidden) {
        const space = `${Math.ceil(this.getBoundingClientRect().height)}px`;
        if (document.documentElement.style.getPropertyValue('--fms-mobile-purchase-space') !== space) {
          document.documentElement.style.setProperty('--fms-mobile-purchase-space', space);
        }
      }
      if (this.overlayOpen && !overlayOpen && this.restoreFocus) {
        if (!this.hidden && (document.activeElement === document.body || !this.visible(document.activeElement))) {
          this.restoreFocus.focus({ preventScroll: true });
        }
        this.restoreFocus = null;
      }
      this.overlayOpen = overlayOpen;
      if (this.addAttempted) {
        const error = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        if (error && !error.hidden && !loading && !this.blockedByOverlay()) {
          this.addAttempted = false;
          this.restoreFocus = null;
          this.goTo(error);
        }
      }
    }

    goTo(element) {
      if (!element) return;
      element.scrollIntoView({ block: 'center', behavior: 'instant' });
      const focusTarget = element.matches('input,select,button') ? element : element.querySelector('input:checked,select,button,input');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      else { element.setAttribute('tabindex', '-1'); element.focus({ preventScroll: true }); }
      this.schedule();
    }

    onClick(event) {
      if (event.target.closest('[data-edit-options]')) this.goTo(this.productInfo.querySelector('variant-selects'));
      if (event.target.closest('[data-edit-quantity]')) this.goTo(this.originalQuantity);
      if (!event.target.closest('[data-submit]')) return;
      this.sync();
      if (this.hidden || this.originalSubmit.disabled || this.originalSubmit.getAttribute('aria-disabled') === 'true') return;
      const productForm = this.originalSubmit.closest('product-form');
      this.addAttempted = true;
      this.restoreFocus = event.target.closest('[data-submit]');
      if (productForm?.validateQuantity && !productForm.validateQuantity()) {
        this.addAttempted = false;
        this.restoreFocus = null;
        this.goTo(this.originalQuantity);
        return;
      }
      this.originalSubmit.click();
      this.schedule();
    }
  });
}
